import { Meter as BaseMeter } from "@base-ui/react/meter";
import clsx from "clsx";
import type { ReactNode } from "react";
import { Decimal, type DecimalString } from "@/lib/decimal";
import styles from "./Meter.module.css";

export type MeterLevel = "ok" | "low" | "empty";

export interface MeterProps {
  label: ReactNode;
  hideLabel?: boolean;
  /** Current amount (a decimal string from the API is fine; display only). */
  value: number | DecimalString;
  min?: number;
  max: number | DecimalString;
  /** At or below this the meter shows the low (warning) level. */
  low?: number | DecimalString;
  /** Override the computed level. */
  level?: MeterLevel;
  /** The text read out and shown next to the label: "12.300 g of 50 g". */
  valueText: string;
  size?: "sm" | "md";
  className?: string;
}

function toNumber(value: number | DecimalString | undefined): number | undefined {
  if (value === undefined) return undefined;
  return typeof value === "number" ? value : (Decimal.tryFrom(value)?.toNumber() ?? 0);
}

/** A measured amount within a known range, such as stock left in a lot (Base UI Meter). */
export function Meter({
  label,
  hideLabel,
  value,
  min = 0,
  max,
  low,
  level,
  valueText,
  size = "md",
  className,
}: MeterProps) {
  const current = toNumber(value) ?? 0;
  const maximum = Math.max(toNumber(max) ?? 1, min);
  const lowThreshold = toNumber(low);
  const computedLevel: MeterLevel =
    level ?? (current <= min ? "empty" : lowThreshold !== undefined && current <= lowThreshold ? "low" : "ok");
  return (
    <BaseMeter.Root
      value={Math.min(Math.max(current, min), maximum)}
      min={min}
      max={maximum}
      getAriaValueText={() => valueText}
      className={clsx(styles.meter, className)}
      data-level={computedLevel}
      data-size={size}
    >
      <div className={styles.header}>
        <BaseMeter.Label className={hideLabel ? styles.hiddenLabel : styles.label}>{label}</BaseMeter.Label>
        <span className={styles.value}>{valueText}</span>
      </div>
      <BaseMeter.Track className={styles.track}>
        <BaseMeter.Indicator className={styles.indicator} />
      </BaseMeter.Track>
    </BaseMeter.Root>
  );
}
